/**
 * Frontend-only types and helpers.
 *
 * Wire protocol types are generated from Python dataclasses in protocol.py.
 */

export * from "./protocol.generated";

/**
 * Mesh format for an exported ROS package.
 *
 * Narrows `ExportUrdfMessage.visual_mesh_format` /
 * `.collision_mesh_format`, which the generator can only type as
 * `string`. Must stay in sync with `MESH_FORMATS` in
 * `robot_compiler/viewer/urdf_export.py`.
 */
export type MeshFormat = "glb" | "dae" | "stl";

/**
 * The formats a mesh can be converted *to*.
 *
 * `glb` is absent on purpose: converting to GLB would drop the Draco
 * compression the stored assets carry, so GLB only ever means "leave the
 * mesh alone" — which the server requires for both geometries or neither.
 */
export const CONVERTIBLE_MESH_FORMAT_OPTIONS: {
  value: Exclude<MeshFormat, "glb">;
  label: string;
  description: string;
}[] = [
  {
    value: "dae",
    label: "COLLADA / DAE",
    description: "Keeps per-vertex colors. Loads in RViz and Gazebo.",
  },
  {
    value: "stl",
    label: "STL",
    description: "Geometry only — drops colors and materials, but the most portable.",
  },
];

// GUI Element types for state management
export type GuiElementType =
  | "folder"
  | "button"
  | "slider"
  | "dropdown"
  | "checkbox"
  | "text";

export interface GuiElementBase {
  uuid: string;
  type: GuiElementType;
  container_uuid: string;
  panel: "left" | "right";
  order: number;
}

export interface GuiFolderElement extends GuiElementBase {
  type: "folder";
  label: string;
  expand_by_default: boolean;
}

export interface GuiButtonElement extends GuiElementBase {
  type: "button";
  label: string;
  disabled: boolean;
  color: string | null;
}

export interface GuiSliderElement extends GuiElementBase {
  type: "slider";
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  disabled: boolean;
}

export interface GuiDropdownElement extends GuiElementBase {
  type: "dropdown";
  label: string;
  options: string[];
  value: string;
  disabled: boolean;
}

export interface GuiCheckboxElement extends GuiElementBase {
  type: "checkbox";
  label: string;
  value: boolean;
  disabled: boolean;
}

export interface GuiTextElement extends GuiElementBase {
  type: "text";
  content: string;
}

export type GuiElement =
  | GuiFolderElement
  | GuiButtonElement
  | GuiSliderElement
  | GuiDropdownElement
  | GuiCheckboxElement
  | GuiTextElement;

// Mesh data for 3D scene (supports both vertex/face and GLB formats)
export interface MeshData {
  uuid: string;
  // Legacy format (vertices/faces)
  vertices?: number[][];
  faces?: number[][];
  // GLB format (content-addressed URL)
  glb_url?: string;
  // Common properties
  color: [number, number, number] | null;
  opacity: number;
  position: [number, number, number];
  quaternion: [number, number, number, number];
  parent_uuid: string | null;
  instance_id: string | null;
  // URDF link this mesh draws. Present so a touched mesh can name a link to
  // the server, which is otherwise only addressable by name.
  link_name?: string | null;
}

// Type guard for GLB mesh data
export function isGlbMesh(mesh: MeshData): boolean {
  return mesh.glb_url !== undefined;
}

// Camera state
export interface CameraState {
  position: [number, number, number];
  target: [number, number, number];
}
